/**
 * Manifest-driven presentation layer for tabular widgets (MER-438).
 *
 * Table widgets used to render raw warehouse rows: tenant_id UUIDs, machine
 * column headers (event_*, drive_*), snake_case enums (front_load), 15-column
 * overflow, unformatted ISO dates / numbers. This module turns a widget + its
 * raw rows into customer-ready columns and display strings:
 *
 *   - Column allow-list: a widget's declared `columns` (ordered) is the only set
 *     that renders. With no allow-list, internal plumbing (HIDDEN_COLUMNS) is
 *     dropped so tenant_id never reaches a customer.
 *   - Headers: per-column `label` override, else the column name with _id/_pct
 *     noise stripped and `humanize()` applied ("gross_margin_pct" -> "Gross Margin").
 *   - Cells: currency / fraction-aware percent (MER-184) / integer / decimal /
 *     ratio via `formatValue`; ISO dates via `formatTableDate`; snake_case enums
 *     via `humanize`. Format comes from a `column_overrides` entry or name inference.
 *   - Layout: numeric columns right-align; the table caps at MAX_TABLE_COLUMNS.
 *
 * Pure (no React / DOM) so the decision is type-checked and reviewable; the
 * component (`PresentedTable`) only renders the result.
 */

import {
  type KpiFormat,
  asKpiFormat,
  formatTableDate,
  formatValue,
  formatWeekStart,
  inferFormatFromName,
} from "./format";
import { INTERNAL_COLUMNS } from "./drillQuery.mjs";
import { humanize } from "./labels";
import type { ManifestWidget } from "./manifest";
import { defaultBoundsForColumn, resolveTileGate } from "./sanityGate";

/**
 * Internal plumbing columns that must NEVER reach a customer, even when a widget
 * declares no explicit allow-list. tenant_id is the UUID culprit from MER-438;
 * the rest is record / period machinery.
 *
 * MER-222: the list itself now lives in drillQuery.mjs, because the drill has
 * to drop these columns from the PROJECTION (before the request is sent) and
 * this layer drops them from the RENDER (after the response arrives). Two
 * hand-copied lists of what must never reach a customer is one list too many;
 * there used to be a third in table-presentation.test.mjs under a "keep in
 * sync" comment, and it is gone for the same reason.
 */
export const HIDDEN_COLUMNS: ReadonlySet<string> = new Set(INTERNAL_COLUMNS);

// Internal coverage markers that must never render as a cell value.
// MER-633/635 added FEED_ANOMALY (disposal magnitude impossible) and
// FEED_INCOMPLETE (invoice feed starved). They are stamped ahead of
// SUPPRESSED / PARTIAL_DATA on the same column, so a row that used to be
// caught here stopped being caught the day those markers shipped.
const SUPPRESSION_MARKERS = /\b(SUPPRESSED|PARTIAL_DATA|FEED_ANOMALY|FEED_INCOMPLETE)\b/;

// Equipment / LOB columns whose "unknown" value marks a noise row to drop.
const EQUIPMENT_KEYS = ["equipment_type", "line_of_business", "lob"];

// Period columns, in priority order, used for the deterministic period-desc sort.
const PERIOD_KEYS = ["period_date", "period_start", "period"];

/**
 * A table past this many columns is an unreadable horizontal overflow. The
 * allow-list order is the priority; extras are dropped and surfaced via
 * `hiddenByCap` so the cap is never silent.
 *
 * 9, not 8: the three widest tabular widgets (L0.5b and L6a.2 Collections
 * Queue, L3b.3) each declare exactly 9 columns, so a cap of 8 silently sliced
 * `bill_group` off all three. Every other tabular widget declares 7 or fewer.
 * scripts/table-presentation.test.mjs now fails if a manifest declares more
 * columns than this, so the next widget to exceed it is a red build rather
 * than a column that quietly disappears.
 */
export const MAX_TABLE_COLUMNS = 9;

export type ColumnAlign = "left" | "right";

/** Per-column override declared in the manifest under `column_overrides`. */
export interface ColumnOverride {
  label?: string;
  /** A display_format alias (currency / percent / integer / ...). */
  format?: string;
  align?: ColumnAlign;
}

export interface PresentedColumn {
  /** Raw row key. */
  key: string;
  /** Customer-facing header. */
  label: string;
  align: ColumnAlign;
}

export interface PresentedTable {
  columns: PresentedColumn[];
  /** Display-ready strings, row-major, aligned to `columns`. null = empty cell. */
  rows: (string | null)[][];
  /** Columns dropped by the cap (0 when none). */
  hiddenByCap: number;
}

export interface PresentOptions {
  /** Demo-mode PII hook: (value, column) -> masked value. */
  obfuscate?: (value: unknown, column: string) => unknown;
  /**
   * Column cap (default MAX_TABLE_COLUMNS). The drill-down passes Infinity to
   * show every detail column rather than a curated summary subset.
   */
  maxColumns?: number;
  /**
   * When false, ignore the widget's `columns` allow-list and show all non-hidden
   * columns. The drill-down wants the underlying detail, not the summary table's
   * curated subset -- but tenant_id and the other HIDDEN_COLUMNS are still dropped.
   */
  useAllowList?: boolean;
  /**
   * MER-571: when false, skip the egress sanity gate on cell values. Defaults to
   * ON. Only the Data Quality surface, whose whole job is to show the bad number,
   * should ever turn this off.
   */
  gateCells?: boolean;
}

const NUMERIC_FORMATS: ReadonlySet<KpiFormat> = new Set<KpiFormat>([
  "currency_usd",
  "percent",
  "integer",
  "decimal_1",
  "decimal_2",
  "ratio",
]);

function declaredAllowList(widget: ManifestWidget): string[] | undefined {
  const c = (widget as { columns?: unknown }).columns;
  return Array.isArray(c) && c.length > 0 ? c.map((x) => String(x)) : undefined;
}

function overridesOf(widget: ManifestWidget): Record<string, ColumnOverride> {
  const o = (widget as { column_overrides?: unknown }).column_overrides;
  return o && typeof o === "object" ? (o as Record<string, ColumnOverride>) : {};
}

/** Customer-facing header: override label, else strip _id/_pct noise + humanize. */
function headerLabel(
  key: string,
  override: ColumnOverride | undefined,
  isPercent: boolean,
): string {
  if (override?.label) return override.label;
  let base = key;
  if (isPercent && /_pct$/i.test(base)) base = base.replace(/_pct$/i, "");
  else if (/_id$/i.test(base)) base = base.replace(/_id$/i, "");
  return humanize(base);
}

/** Resolve a column's KpiFormat from an override or name inference (may be null). */
function columnFormat(key: string, override: ColumnOverride | undefined): KpiFormat | null {
  if (override?.format) return asKpiFormat(override.format);
  return inferFormatFromName(key, undefined);
}

function formatCell(value: unknown, fmt: KpiFormat | null): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") {
    if (fmt) return formatValue(value, fmt);
    return Number.isInteger(value)
      ? formatValue(value, "integer")
      : formatValue(value, "decimal_2");
  }
  if (typeof value === "string") {
    // MER-438 addendum: internal coverage markers ("SUPPRESSED", "$0 SUPPRESSED",
    // "PARTIAL_DATA") must read as an empty cell, never as customer text.
    if (SUPPRESSION_MARKERS.test(value)) return null;
    const asDate = formatTableDate(value);
    if (asDate) return asDate;
    if (fmt && fmt !== "text" && value.trim() !== "" && !Number.isNaN(Number(value))) {
      return formatValue(Number(value), fmt);
    }
    return humanize(value);
  }
  return String(value);
}

/**
 * Turn a tabular widget + its raw rows into customer-ready columns and strings.
 */
export function presentTable(
  widget: ManifestWidget,
  rows: Record<string, unknown>[],
  opts: PresentOptions = {},
): PresentedTable {
  const allKeys = rows.length > 0 ? Object.keys(rows[0]) : [];
  const useAllowList = opts.useAllowList ?? true;
  const gateCells = opts.gateCells ?? true;
  const maxColumns = opts.maxColumns ?? MAX_TABLE_COLUMNS;
  const allow = useAllowList ? declaredAllowList(widget) : undefined;
  const overrides = overridesOf(widget);

  // MER-438 addendum: drop "unknown"-equipment noise rows by default, then sort
  // newest period first so the order is deterministic (snapshot tables rendered
  // periods in random order). Period columns are TEXT (YYYY-MM) or ISO, so a
  // lexical desc compare is correct for both.
  const equipKeys = EQUIPMENT_KEYS.filter((k) => allKeys.includes(k));
  let dataRows = rows.filter(
    (r) => !equipKeys.some((k) => String(r[k] ?? "").toLowerCase() === "unknown"),
  );
  const periodKey = PERIOD_KEYS.find((k) => allKeys.includes(k));
  if (periodKey) {
    dataRows = [...dataRows].sort((a, b) =>
      String(b[periodKey] ?? "").localeCompare(String(a[periodKey] ?? "")),
    );
  }

  // Internal plumbing is dropped even on the allow-list path as a backstop, so
  // a stray tenant_id in a hand-authored allow-list can never reach a customer.
  let keys = (allow ? allow.filter((k) => allKeys.includes(k)) : allKeys).filter(
    (k) => !HIDDEN_COLUMNS.has(k),
  );
  const hiddenByCap = Math.max(0, keys.length - maxColumns);
  keys = keys.slice(0, maxColumns);

  const columns: PresentedColumn[] = keys.map((key) => {
    const ov = overrides[key];
    const fmt = columnFormat(key, ov);
    const numeric =
      (fmt !== null && NUMERIC_FORMATS.has(fmt)) || rows.some((r) => typeof r[key] === "number");
    const align: ColumnAlign = ov?.align ?? (numeric ? "right" : "left");
    return { key, label: headerLabel(key, ov, fmt === "percent"), align };
  });

  const presentedRows: (string | null)[][] = dataRows
    .map((r) =>
      keys.map((key) => {
        const ov = overrides[key];
        const fmt = columnFormat(key, ov);
        let v = r[key];
        if (opts.obfuscate) {
          const masked = opts.obfuscate(v, key);
          // A changed string is the masked id; render it as-is, not re-humanized.
          if (typeof masked === "string" && masked !== v) return masked;
          v = masked;
        }
        // MER-438 addendum: a weekly row's period cell rendered as a bare day
        // ("May 4"); label it "Week of May 4". Per-row period_type so a mixed
        // set never mislabels a monthly row.
        if (
          key === periodKey &&
          typeof v === "string" &&
          String(r["period_type"] ?? "").toLowerCase() === "weekly"
        ) {
          const wk = formatWeekStart(v);
          if (wk) return wk;
        }
        // MER-571: gate the cell the same way a KPI tile gates its scalar. The
        // display-time bounds only ever ran on WidgetRenderer's single_number
        // branch, so the identical starved-denominator value was suppressed as a
        // tile and printed unchallenged as a table cell -- and the drill-down is
        // a table, which is how "Not available" and $46,188.52/ton appeared one
        // click apart. Same resolver, same bounds, same copy.
        // NB: do NOT condition this on a format being inferred. columnFormat
        // returns null for names it cannot classify -- including
        // disposal_cost_per_ton -- and gating only formatted cells left exactly
        // the column that leaked still printing 46188.52. Bounds are a property
        // of the COLUMN, not of how we chose to format it.
        if (gateCells && typeof v === "number") {
          const bounds = widget.display_bounds ?? defaultBoundsForColumn(key);
          const cellGate = resolveTileGate(v, fmt ?? "decimal_2", { column: key, bounds });
          if (cellGate) return cellGate.copy;
        }
        return formatCell(v, fmt);
      }),
    )
    // MER-438 addendum: drop all-empty rows so historical snapshot periods with
    // no values don't render as walls of blank cells.
    .filter((row) => row.some((cell) => cell !== null));

  return { columns, rows: presentedRows, hiddenByCap };
}
